import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  type RouterHistory,
  redirect,
} from '@tanstack/react-router'
import { BrandingScreen } from './branding/branding-screen.tsx'
import { FlagsScreen } from './flags/flags-screen.tsx'
import { safeNextPath } from './lib/next-path.ts'
import type { PlatformSessionStore } from './lib/session.ts'
import { AccountScreen } from './screens/account.tsx'
import { NotFoundScreen } from './screens/not-found.tsx'
import { SignInScreen } from './screens/sign-in.tsx'
import { useSession } from './session/context.tsx'
import { AppShell } from './shell/app-shell.tsx'
import { CreateTenantScreen } from './tenants/create-tenant.tsx'
import { TenantListScreen } from './tenants/tenant-list.tsx'
import { TenantPage } from './tenants/tenant-page.tsx'
import { TenantSettingsScreen } from './tenants/tenant-settings.tsx'

/**
 * The routes, from §3's screen inventory. Eleven surfaces in the finished application;
 * the ones below are the shell and its two ends.
 *
 * | Route | Screen | Built by |
 * |---|---|---|
 * | `/login` | Sign in | P01 |
 * | `/` | redirects to `/tenants` | P01 |
 * | `/tenants` | Tenants | P02 |
 * | `/tenants/new` | Create a tenant | P02 |
 * | `/tenants/:tenantId` | Tenant | P02, plus its "Build key" panel (`build-key`) |
 * | `/tenants/:tenantId/settings` | Settings | P02 |
 * | `/tenants/:tenantId/branding` | Branding | P04 (read/preview/download), finished by P04b (edit form) against P04a-api |
 * | `/tenants/:tenantId/flags` | Feature flags | P04 (placeholder), replaced by P04b (real screen) against P04a-api |
 * | `/account` | Your account | P01 |
 * | anything else | Not found | P01 |
 *
 * **P03's `/tenants/:tenantId/build-identifier` and `/keys` routes are gone.** Both were
 * built against the signed, `kid`-rotated build identifier and its platform-wide signing
 * keys — the model `build-key` (`docs/DECISIONS.md`, "The build identifier is a plain
 * rotatable key on the tenant row, not a signed, kid-rotated token") replaced with a plain
 * `tenants.build_key` column and no signing keys at all. There is nothing platform-wide
 * left to list, and nothing to mint per call: rotating **is** the only action, it is
 * tenant-scoped, and it now lives as a panel on the tenant's own page (`build-key`) rather
 * than as two of its own routes.
 *
 * Written as a route tree in TypeScript rather than as files on disk. The file-based
 * convention would put loaders, guards and parameter parsing in generated route files,
 * which is precisely the code docs/TESTING.md §1 says must stay measurable — so the
 * guards live here and in the components, both of which are in the coverage denominator.
 */
export type RouterContext = {
  readonly session: PlatformSessionStore
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: RouterNotFound,
})

/**
 * The page for an address no route matches (§18.3), the same page a missing tenant id gets.
 *
 * **Signed in, it is drawn inside the shell** — masthead, environment word and rail (§5.1) — like
 * every other signed-in page; it used to be the bare card, because an unmatched address has no
 * route in the signed-in group to render through (P08 mobile QA, D1). **Signed out it stays the
 * bare card**: a stranger gets the same sentence and nothing about the console around it, and
 * `AppShell` would redirect them to sign in, which an unknown address is not a reason for.
 */
function RouterNotFound() {
  const session = useSession()
  return session.tokens === undefined ? <NotFoundScreen /> : <AppShell page={<NotFoundScreen />} />
}

/**
 * The root of the route tree: it renders whatever route matched, at every width.
 *
 * There used to be a width gate here, rendering one plain "this is a desktop tool" page below
 * 1024 CSS pixels (P08 retired it: docs/DECISIONS.md, "The platform console is usable on a
 * phone, and the desktop-only gate is retired"). Its history is why this component is still a
 * named function rather than `Outlet` inline: the gate once unmounted the router, and with it
 * the router's only subscription to the browser history, so Back pressed while narrow moved the
 * URL and not the screen (`fix-console-narrow-history-tenant-carryover`, QA D4). With nothing
 * that unmounts the router at any width, that failure has no way to arise; the history tests
 * that guarded it (`shell/tenant-history.test.tsx`) stay, because the *consequence* it had —
 * a Save sent to the tenant that was shown before, not the one in the address bar — is a tenant
 * isolation failure whichever mechanism produces it.
 */
function RootLayout() {
  return <Outlet />
}

/** Where a signed-out visitor is sent, and where they come back from. */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // The key is always present in the result, even when it is `undefined`. A route's
  // search is *merged* over its parent's, and the root route parses the raw query — so
  // returning `{}` for a value this route refuses would leave the raw one showing
  // through from above, which is precisely the value being refused.
  validateSearch: (search: Record<string, unknown>): { next: string | undefined } => ({
    next: safeNextPath(search.next),
  }),
  component: LoginRoute,
})

function LoginRoute() {
  const { next } = loginRoute.useSearch()
  return <SignInScreen {...(next === undefined ? {} : { next })} />
}

/**
 * Everything behind a session. A route reached while signed out redirects here before it
 * loads, so there is no flash of a table on the way (§18.2) — and `AppShell` repeats the
 * check for a session that ends while a page is already open.
 */
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: ({ context, location }) => {
    if (!context.session.isSignedIn()) {
      throw redirect({
        to: '/login',
        search: { next: location.pathname },
        replace: true,
      })
    }
  },
  component: AppShell,
})

const indexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  // There is no dashboard: a list of tenants *is* the estate, and a second screen
  // summarising it would be a second thing to keep true (§3).
  beforeLoad: () => {
    throw redirect({ to: '/tenants', replace: true })
  },
})

/**
 * The estate (§7), plus the three search parameters that carry a recovery into it.
 *
 * `q` prefills the search, `state` widens the filter and `rescue` says why the owner
 * arrived — together they are how §8.4's and §20.4's "look this slug up" lands somewhere
 * useful rather than on an unfiltered list. All three are parsed defensively, like `next`
 * on `/login`: a value from a URL is never trusted to be the type a screen expects.
 */
const tenantsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tenants',
  // Every key is optional, so `<Link to="/tenants">` needs none of them: the masthead and
  // the not-found page link here without a recovery to carry.
  validateSearch: (search: Record<string, unknown>): TenantListSearch => ({
    ...(typeof search.q === 'string' && search.q !== '' ? { q: search.q } : {}),
    ...(isStateFilter(search.state) ? { state: search.state } : {}),
    ...(search.rescue === true || search.rescue === 'true' ? { rescue: true } : {}),
  }),
  component: TenantsRoute,
})

type TenantStateFilter = 'live' | 'retired' | 'any'

type TenantListSearch = {
  readonly q?: string
  readonly state?: TenantStateFilter
  readonly rescue?: true
}

function isStateFilter(value: unknown): value is TenantStateFilter {
  return value === 'live' || value === 'retired' || value === 'any'
}

function TenantsRoute() {
  const { q, state, rescue } = tenantsRoute.useSearch()
  return (
    <TenantListScreen
      {...(q === undefined ? {} : { q })}
      {...(state === undefined ? {} : { initialState: state })}
      rescue={rescue === true}
    />
  )
}

/** Creating one — a page rather than a dialog (§8.1). */
const createTenantRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tenants/new',
  component: CreateTenantScreen,
})

const tenantRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tenants/$tenantId',
  component: TenantRoute,
})

function TenantRoute() {
  const { tenantId } = tenantRoute.useParams()
  return <TenantPage tenantId={tenantId} />
}

const tenantSettingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tenants/$tenantId/settings',
  component: TenantSettingsRoute,
})

function TenantSettingsRoute() {
  const { tenantId } = tenantSettingsRoute.useParams()
  return <TenantSettingsScreen tenantId={tenantId} />
}

const brandingRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tenants/$tenantId/branding',
  component: BrandingRoute,
})

function BrandingRoute() {
  const { tenantId } = brandingRoute.useParams()
  return <BrandingScreen tenantId={tenantId} />
}

const flagsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tenants/$tenantId/flags',
  component: FlagsRoute,
})

function FlagsRoute() {
  const { tenantId } = flagsRoute.useParams()
  return <FlagsScreen tenantId={tenantId} />
}

const accountRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/account',
  component: AccountScreen,
})

export const routeTree = rootRoute.addChildren([
  loginRoute,
  authenticatedRoute.addChildren([
    indexRoute,
    tenantsRoute,
    createTenantRoute,
    tenantRoute,
    tenantSettingsRoute,
    brandingRoute,
    flagsRoute,
    accountRoute,
  ]),
])

export type AppRouterOptions = {
  readonly session: PlatformSessionStore
  /** A memory history, in a test. The browser's own, in the application. */
  readonly history?: RouterHistory
}

export function createAppRouter(options: AppRouterOptions) {
  return createRouter({
    routeTree,
    context: { session: options.session },
    defaultNotFoundComponent: RouterNotFound,
    ...(options.history === undefined ? {} : { history: options.history }),
  })
}

export type AppRouter = ReturnType<typeof createAppRouter>

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter
  }
}
